// src/cockpit4/components/YesterdayLearningsCard.tsx
// ============================================
// "昨天公司学到了什么"卡片
//
// 依据白皮书第十五章《Knowledge Evolution Protocol》+ 总控台功能点 6：
//   每天 08:00 显示知识演化 Runtime 生成的"今天公司学到了什么"报告摘要，
//   点击可查看完整内容。
//
// 数据来源（禁止假数据）：
//   RuntimeManager.query(KnowledgeEvolution, get_daily_report / get_stats)
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, X, Loader2, RefreshCw } from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { KnowledgeEvolutionQuery } from "../../runtime/KnowledgeEvolutionRuntime";

interface DailyReport {
  date: string;
  generatedAt: string;
  newCandidates: number;
  autoApproved: number;
  needsReview: number;
  needsCeo: number;
  insights: Array<{ title: string; summary: string; source: string }>;
  vaultPath: string;
}

const SOURCE_ICON: Record<string, string> = {
  hermes_dialogue: "💬",
  runtime_log: "🖥️",
  review_feedback: "📝",
  git_commit: "🔧",
  customer_follow_up: "🤝",
};

export default function YesterdayLearningsCard() {
  const [report, setReport] = useState<DailyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
      const resp = await RuntimeManager.query(RuntimeName.KnowledgeEvolution, {
        type: KnowledgeEvolutionQuery.GetDailyReport,
        date,
      } as any);
      // 双层解包：RuntimeManager 包一层 { success, data }，
      // KnowledgeEvolutionRuntime 再包一层 { success, data: report } → report 在 resp.data.data
      if (resp?.success) {
        const outer = resp.data as any;
        const inner = outer && typeof outer === "object" && "data" in outer ? outer : null;
        const reportData: any = inner?.success ? inner.data : outer;
        if (reportData && typeof reportData === "object") {
          setReport(reportData as DailyReport);
        } else {
          setReport(null);
        }
      }
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <BookOpen size={12} className="text-purple-400" />
          <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider">
            昨天公司学到了什么
          </span>
          <span className="text-[9px] font-mono text-gray-600">知识演化 Runtime · 23:00</span>
        </div>
        <button
          onClick={load}
          className="p-1 rounded text-gray-500 hover:text-gray-300 transition-colors"
          title="刷新"
        >
          <RefreshCw size={10} />
        </button>
      </div>

      {loading ? (
        <div className="mt-2 flex items-center gap-2 text-[10px] font-mono text-gray-500">
          <Loader2 size={10} className="animate-spin" /> 加载中...
        </div>
      ) : !report ? (
        <p className="mt-2 text-[10px] font-mono text-gray-500">
          暂无日报 · 知识演化 Runtime 每天 23:00 自动生成
        </p>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {[
              { label: "新增候选", value: report.newCandidates, color: "#A78BFA" },
              { label: "自动入库", value: report.autoApproved, color: "#34D399" },
              { label: "待确认", value: report.needsReview, color: "#FBBF24" },
              { label: "必须确认", value: report.needsCeo, color: "#F87171" },
            ].map((s) => (
              <div key={s.label} className="px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.05] text-center">
                <div className="text-[13px] font-mono font-semibold" style={{ color: s.color }}>
                  {s.value}
                </div>
                <div className="text-[8px] font-mono text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>

          {(report.insights || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {(report.insights || []).slice(0, 3).map((ins, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10px] font-mono text-gray-400">
                  <span>{SOURCE_ICON[ins.source] || "📌"}</span>
                  <span className="line-clamp-1">{ins.title}</span>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setExpanded(true)}
            className="mt-2 text-[9px] font-mono text-purple-400 hover:text-purple-300 transition-colors"
          >
            查看完整内容 →
          </button>

          {/* 完整日报弹窗 */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                onClick={() => setExpanded(false)}
              >
                <motion.div
                  initial={{ scale: 0.96, y: 10 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.96, y: 10 }}
                  className="w-full max-w-lg max-h-[70vh] overflow-y-auto rounded-xl border border-white/[0.08] bg-[#0d1526] p-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[12px] font-mono text-purple-300">
                      今天公司学到了什么（{report.date}）
                    </span>
                    <button
                      onClick={() => setExpanded(false)}
                      className="p-1 rounded text-gray-500 hover:text-gray-300"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(report.insights || []).length === 0 ? (
                      <p className="text-[11px] font-mono text-gray-500">当日未提取到新的候选知识。</p>
                    ) : (
                      (report.insights || []).map((ins, i) => (
                        <div key={i} className="px-2.5 py-2 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                          <div className="flex items-center gap-1.5 text-[10px] font-mono text-gray-200">
                            <span>{SOURCE_ICON[ins.source] || "📌"}</span>
                            <span>{ins.title}</span>
                          </div>
                          <p className="mt-1 text-[10px] font-mono text-gray-500">{ins.summary}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <p className="mt-3 text-[9px] font-mono text-gray-600 break-all">
                    {report.vaultPath}
                  </p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
